<template>
	<Modal
		:title="title"
		:closeOnOverlayClick="false"
		:visible="visible"
		@close="onCancel"
	>
		<template v-slot:body>
			<div class="credit-report-bureaus">
				<p
					v-if="singleBureau"
					class="credit-report-bureaus__header"
				>
					If the Applicant or
					Co-Applicant has a freeze on their credit, enter the PIN to
					release it.
				</p>
				<p
					v-else
					class="credit-report-bureaus__header"
				>
					Select at least one credit bureau. If the Applicant or
					Co-Applicant has a freeze on their credit, enter the PIN to
					release it.
				</p>
				<NotificationBlock state="time" iconClassName="icon-info">
					<div class="credit-report-bureaus__infoMessage">
						PINs will not be saved. You must enter PINs for each
						submission.
						<span class="credit-report-bureaus__unfreeze-text">
							<span class="credit-report-bureaus__bold"
								>NOTE:</span
							>
							The customer can also unfreeze their credit using
							the {{title}} app or website. After doing so,
							no PIN will be required here.
						</span>
					</div>
				</NotificationBlock>
				<div v-if="singleBureau" class="credit-report-bureaus__inputs">
					<InputPassword
						labelText="Applicant"
						name='inputApplicantApplicant'
						:autocomplete="'new-password'"
						class="field-container__input"
						@change="onAppPinChange"
						@validate="onValidate"
						:validators="PINValidators"
						ref="refBureaus"
					/>
					<InputPassword
						labelText="Co-Applicant"
						name='inputApplicantCo-Applicant'
						:autocomplete="'new-password'"
						class="field-container__input"
						@change="onCoAppPinChange"
						@validate="onValidate"
						:validators="PINValidators"
						ref="refBureaus"
					/>
				</div>
				<div v-else class="credit-report-bureaus__checkboxes">
					<div v-for="bureau in integrations" v-bind:key="bureau">
						<ToggleFieldsForm
							:title="bureau"
							@change="providerSelected"
							@blurred="handleBur"
						/>
					</div>
				</div>
			</div>
		</template>
		<template v-slot:footer>
			<div>
				<ButtonSet>
					<template>
						<div
							ref="refDivbtnSubmit"
							@mouseenter="showSubmitTooltip = true"
							@mouseleave="showSubmitTooltip = false"
						>
							<ButtonClassic
								id="submitButton"
								:disabled="!bureauSelected"
								variant="primary"
								labelText="Submit"
								@click="onSubmitClick"
								ref="btnSubmit"
							/>
						</div>
						<ButtonClassic
							variant="text"
							labelText="Cancel"
							@click="onCancel"
						/>
					</template>
				</ButtonSet>
			</div>
			<Tooltip
				placement="top"
				:visible="tooltipVisible"
				:trackedRefName="$refs.refDivbtnSubmit"
				positionStrategy="fixed"
				:offset="[0, 0]"
				state="negative"
				@mouseenter="showSubmitTooltip = true"
				@mouseleave="showSubmitTooltip = false"
			>
				<span v-if="!bureauSelected">Select at least one bureau</span>
			</Tooltip>
			<CreditReportSelection
				:visible="creditReportSelectionVisible"
				@cancel="onSelectionCancel"
				@ready="onSelectionReady"
				@submit="onSelectionSubmit"
				@complete="onSelectionComplete"
			/>
		</template>
	</Modal>
</template>

<script>
import Modal from '@/components/shells/modal/modal.vue';
import ButtonSet from '@/components/forms/button/button-set.vue';
import InputPassword from '@/components/forms/input/input-password.vue';
import ButtonClassic from '@/components/forms/button/button-classic.vue';
import { mapGetters, mapActions } from 'vuex';
import viewMixin from '@/mixins/view-mixin';
import messageHandlerMixin from '@/mixins/message-handler-mixin';
import pageMixin from '@/mixins/page-mixin';
import ToggleFieldsForm from './toggle-fields-form.vue';
import NotificationBlock from '@/components/notifications/notification-block/notification-block.vue';
import CreditReportSelection from './credit-report-selection-modal.vue';
import Tooltip from '@/components/shells/tooltip/tooltip.vue';
import { NumericValidator } from '@/validators/index.js';
import LengthValidator from '@/validators/length-validator';

export default {
	name: 'CreditReportBureaus',
	mixins: [viewMixin, messageHandlerMixin, pageMixin],
	components: {
		Modal,
		ButtonSet,
		ButtonClassic,
		ToggleFieldsForm,
		NotificationBlock,
		CreditReportSelection,
		Tooltip,
		InputPassword
	},
	data() {
		return {
			creditReportSelectionVisible: false,
			bureauInfo: [],
			singleBureauInputs: {
				appPIN: '',
				coAppPIN: ''
			},
			isFormValid: false,
			vState: [],
			showSubmitTooltip: false,
			selectedOptions: {
				bureauInfo: [],
				submitTo: ''
			}
		};
	},
	props: {
		id: {
			type: Number,
			required: true
		},
		visible: {
			type: Boolean,
			default: true
		},
		integrations: {
			type: Array,
			required: true,
			default: () => null
		},
		vueSupportRep: {
			type: Boolean,
			required: true,
			default: false
		}
	},
	async created() {
		await this.load();
	},
	watch: {
		singleBureau(value) {
			if (value) {
				this.bureauInfo = [{name: this.integrations[0]}];
				this.isFormValid = true;
			}
		}
	},
	computed: {
		...mapGetters('deal', ['integrationsResult']),
		title() {
			if (this.singleBureau) {
				return this.integrations[0];
			} else {
				return "Credit Bureaus";
			}
		},
		bureauSelected() {
			return this.bureauInfo.length != 0 && this.isFormValid;
		},
		canSubmit() {
			return this.bureauInfo.length != 0;
		},
		tooltipVisible() {
			return !this.canSubmit && this.showSubmitTooltip;
		},
		PINValidators() {
 			return [
				new NumericValidator({
					validateOn: 'blur',
					message: 'Entry must be numeric'
				}),
				new LengthValidator({
					minLength: 5,
					maxLength: 10,
					message: 'PINs must be 5-10 numbers',
					allowEmpty: true
				})
			];
		},
		singleBureau() {
			return this.integrations.length === 1;
		}
	},
	methods: {
		...mapActions('deal', ['getIntegrationsAction']),
		async load() {
			this.hidePageLoader();
		},
		onSelectionComplete() {},
		onSelectionCancel() {
			this.creditReportSelectionVisible = false;
		},
		onSelectionReady() {},
		async onSelectionSubmit(selected) {
			this.selectedOptions.submitTo = selected;
			this.creditReportSelectionVisible = false;
			this.onSubmit();
		},
		async onSubmitClick() {
			if (this.vueSupportRep) {
				this.creditReportSelectionVisible = true;
			} else {
				this.onSubmit();
			}
		},
		onCancel() {
			this.$emit('cancel');
		},
		async onSubmit() {
			if(this.singleBureau) {
				this.bureauInfo[0].appPIN = this.singleBureauInputs.appPIN;
				this.bureauInfo[0].coAppPIN = this.singleBureauInputs.coAppPIN;
			}
			this.selectedOptions.bureauInfo = this.bureauInfo;
			for(let i = 0; i < this.bureauInfo.length; i++) {
				delete this.selectedOptions.bureauInfo[i].checked;
			};
			this.$emit('submit', this.selectedOptions);
		},
		providerSelected({values}) {
			if (values.checked) {
				for(let i = 0; i < this.bureauInfo.length; i++) {
					if (this.bureauInfo[i].name === values.name) {
						return;
					}
				}
				this.bureauInfo.push(values);
				this.isFormValid = true;
			} else if (!values.checked){
				for(let i = 0; i < this.bureauInfo.length; i++) {
					if (this.bureauInfo[i].name === values.name) {
						this.bureauInfo.splice(i, 1);
					}
				}
			}
		},
		onAppPinChange(pin) {
			this.singleBureauInputs.appPIN = pin;
		},
		onCoAppPinChange(pin) {
			this.singleBureauInputs.coAppPIN = pin;
		},
		handleBur(value) {
			this.isFormValid = value.isFormValid;
		},
		frVState(validation) {
			let newVState = this.vState.filter(
				s => s?.componentUniqueId !== validation.componentUniqueId
			);

			newVState.push(validation);
			this.vState = newVState;
		},
		IsVStateValid() {
			const vStateWithErrors = this.vState.filter(
				s => !s.valid && s.messages.length > 0
			);

			return vStateWithErrors.length <= 0;
		},
		onValidate(validation) {
			this.frVState(validation);

			this.isFormValid = this.IsVStateValid();
		}
	}
};
</script>

<style lang="scss" scoped>
.credit-report-bureaus {
	max-width: 590px;
	overflow: hidden;

	@include mq($until: md) {
		max-width: 100%;
	}

	&__header {
		@include heading('sm');
		margin-bottom: $gutter-lg;
	}

	&__checkboxes,
	&__inputs {
		display: flex;
		gap: $gutter-xxl;
		margin-top: $gutter-lg;
	}

	&__infoMessage {
		display: flex;
		gap: $gutter-lg;
		flex-direction: column;
	}
	&__unfreeze-text {
		display: block;
	}
	&__bold {
		font-weight: $bold-font-weight;
	}
}
</style>
